import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Save, Loader2, Mail, Send } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

const DEFAULTS = {
  email_address: '',
  display_name: '',
  imap_host: 'imap.hostinger.com',
  imap_port: '993',
  imap_encryption: 'ssl',
  smtp_host: 'smtp.hostinger.com',
  smtp_port: '465',
  smtp_encryption: 'ssl',
  username: '',
  password: '',
  sync_interval_minutes: 5,
};

/**
 * @param {{ employeeId?: number|string, employeeName?: string, self?: boolean, onSaved?: () => void, embedded?: boolean }} props
 * self=true → /crm/mail/settings (super admin own mailbox)
 * else → /crm/employees/:id/mail-settings
 */
export default function CrmEmployeeMailSettings({
  employeeId,
  employeeName,
  self = false,
  onSaved,
  embedded = false,
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [syncStatus, setSyncStatus] = useState(null);

  const apiBase = self ? '/crm/mail/settings' : `/crm/employees/${employeeId}/mail-settings`;

  const load = useCallback(async () => {
    if (!self && !employeeId) return;
    setLoading(true);
    try {
      const res = await api.get(apiBase);
      const d = res.data?.data || {};
      setConfigured(!!d.configured);
      setSyncStatus({
        last_sync_at: d.last_sync_at,
        last_sync_status: d.last_sync_status,
        last_sync_error: d.last_sync_error,
      });
      setForm({
        ...DEFAULTS,
        email_address: d.email_address || '',
        display_name: d.display_name || employeeName || '',
        imap_host: d.imap_host || DEFAULTS.imap_host,
        imap_port: String(d.imap_port || DEFAULTS.imap_port),
        imap_encryption: d.imap_encryption || 'ssl',
        smtp_host: d.smtp_host || DEFAULTS.smtp_host,
        smtp_port: String(d.smtp_port || DEFAULTS.smtp_port),
        smtp_encryption: d.smtp_encryption || 'ssl',
        username: d.username || d.email_address || '',
        password: '',
        sync_interval_minutes: d.sync_interval_minutes ?? 5,
      });
    } catch {
      toast({ title: 'Error', description: 'Failed to load mail settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [apiBase, employeeName, self, toast]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        imap_port: Number(form.imap_port) || 993,
        smtp_port: Number(form.smtp_port) || 465,
        sync_interval_minutes: Number(form.sync_interval_minutes) || 5,
        ...(form.password ? { password: form.password } : {}),
      };
      const res = await api.post(apiBase, payload);
      const d = res.data?.data || {};
      setConfigured(!!d.configured);
      setSyncStatus({
        last_sync_at: d.last_sync_at,
        last_sync_status: d.last_sync_error ? 'error' : (d.last_sync_status || 'ok'),
        last_sync_error: d.last_sync_error || d.sync?.error || null,
      });
      setForm((prev) => ({ ...prev, password: '' }));
      const syncCount = d.sync?.new_messages;
      const syncError = d.sync?.error;
      if (syncError) {
        toast({
          title: 'Saved, sync failed',
          description: syncError,
          variant: 'destructive',
        });
      } else if (typeof syncCount === 'number') {
        toast({
          title: 'Saved',
          description: `Mail settings updated. Imported ${syncCount} message${syncCount === 1 ? '' : 's'}.`,
        });
      } else {
        toast({ title: 'Saved', description: 'Mail settings updated' });
      }
      onSaved?.();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err.response?.data?.message || 'Could not save mail settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const payload = {
        ...form,
        ...(self ? {} : { crm_employee_id: employeeId }),
        imap_port: Number(form.imap_port) || 993,
        smtp_port: Number(form.smtp_port) || 465,
        ...(form.password ? { password: form.password } : { password: '***' }),
      };
      const res = await api.post(`${apiBase}/test`, payload);
      const ok = res.data?.success;
      toast({
        title: ok ? 'Connection OK' : 'Connection failed',
        description: res.data?.message || res.data?.data?.message || '',
        variant: ok ? 'default' : 'destructive',
      });
    } catch (err) {
      toast({
        title: 'Test failed',
        description: err.response?.data?.message || 'Connection test failed',
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  const formBody = (
    <>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Email address</Label>
          <Input
            value={form.email_address}
            onChange={(e) => setForm({ ...form, email_address: e.target.value })}
            className="mt-1.5"
            placeholder="you@company.com"
          />
        </div>
        <div>
          <Label>Display name</Label>
          <Input
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Username</Label>
          <Input
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label>Password</Label>
          <Input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="mt-1.5"
            placeholder={configured ? 'Leave blank to keep existing' : 'Required'}
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 pt-2 border-t">
        <div>
          <Label>IMAP host</Label>
          <Input value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>IMAP port</Label>
          <Input value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>IMAP encryption</Label>
          <Select value={form.imap_encryption} onValueChange={(v) => setForm({ ...form, imap_encryption: v })}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ssl">SSL</SelectItem>
              <SelectItem value="tls">TLS</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label>SMTP host</Label>
          <Input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>SMTP port</Label>
          <Input value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label>SMTP encryption</Label>
          <Select value={form.smtp_encryption} onValueChange={(v) => setForm({ ...form, smtp_encryption: v })}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ssl">SSL</SelectItem>
              <SelectItem value="tls">TLS</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="max-w-xs">
        <Label>Sync interval (minutes)</Label>
        <Input
          type="number"
          min={1}
          max={60}
          value={form.sync_interval_minutes}
          onChange={(e) => setForm({ ...form, sync_interval_minutes: e.target.value })}
          className="mt-1.5"
        />
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        <Button onClick={handleSave} disabled={saving} className={PRIMARY_BTN}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
        <Button variant="outline" onClick={handleTest} disabled={testing}>
          {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Test connection
        </Button>
      </div>
    </>
  );

  if (loading) {
    if (embedded) {
      return (
        <div className="p-6 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading mail settings…
        </div>
      );
    }
    return (
      <Card>
        <CardContent className="p-6 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading mail settings…
        </CardContent>
      </Card>
    );
  }

  if (embedded) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-base font-semibold">
            <Mail className="w-5 h-5" /> Configure Mail
          </div>
          <Badge variant={configured ? 'default' : 'secondary'}>
            {configured ? 'Configured' : 'Not configured'}
          </Badge>
        </div>
        {syncStatus?.last_sync_error && (
          <p className="text-xs text-destructive">Last sync error: {syncStatus.last_sync_error}</p>
        )}
        {formBody}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="w-5 h-5" /> Mail Settings
          </CardTitle>
          <Badge variant={configured ? 'default' : 'secondary'}>
            {configured ? 'Configured' : 'Not configured'}
          </Badge>
        </div>
        {syncStatus?.last_sync_error && (
          <p className="text-xs text-destructive mt-1">Last sync error: {syncStatus.last_sync_error}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {formBody}
      </CardContent>
    </Card>
  );
}
